<template>
  <b-card-actions
    :title="$t('Cobertura de custo')"
    no-actions
    :show-loading="loading"
  >
    <b-row class="mb-1">
      <b-col md="3">
        <FormulateInput
          id="coverage-sale-value"
          v-model="localSaleValue"
          :label="$t('Valor de referência')"
          type="text-number"
          currency="R$"
          :precision="2"
        />
      </b-col>
      <b-col
        md="3"
        class="d-flex align-items-end pb-1"
      >
        <e-button
          :text="$t('Calcular')"
          variant="primary"
          :busy="loading"
          @click="loadCoverage"
        />
      </b-col>
    </b-row>

    <b-alert
      v-if="!applySurcharge"
      show
      variant="warning"
      class="p-1"
    >
      <feather-icon icon="AlertTriangleIcon" />
      {{
        $t(
          'Esta política não repassa acréscimo ao cliente. A margem abaixo é negativa por definição, e representa o custo que a Loja absorve em cada faixa.'
        )
      }}
    </b-alert>

    <b-table
      show-empty
      responsive
      striped
      small
      class="bordered"
      :empty-text="$t('Informe um valor e clique em Calcular')"
      :fields="fields"
      :items="coverageLines"
    >
      <template #cell(installments)="row">
        {{ row.item.installments }}x
        <!--
          A linha de uma parcela usa o custo de operação À VISTA, que é contratualmente diferente do
          de parcelado. Sem a marcação, o salto entre 1x e 2x parece erro de cadastro.
        -->
        <b-badge
          v-if="row.item.usesCashOperationCost"
          variant="light-info"
          class="ml-50"
        >
          {{ $t('à vista') }}
        </b-badge>
      </template>

      <!--
        A margem é o que a tela existe para mostrar: acréscimo cobrado menos custo da adquirente.
        Negativa significa que a Loja está pagando a diferença — e é uma decisão comercial legítima,
        desde que visível. Por isso é destaque, não erro.
      -->
      <template #cell(coverageMarginPoints)="row">
        <span :class="row.item.hasNegativeCoverage ? 'text-danger font-weight-bold' : 'text-success'">
          {{ formatPercent(row.item.coverageMarginPoints) }}
        </span>
      </template>

      <template #cell(coverageMarginValue)="row">
        <span :class="row.item.hasNegativeCoverage ? 'text-danger font-weight-bold' : 'text-success'">
          {{ row.item.coverageMarginValue | currency }}
        </span>
      </template>

      <template #cell(detail)="row">
        <e-button
          :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'"
          variant="outline-primary"
          size="sm"
          @click="row.toggleDetails"
        />
      </template>

      <!--
        Detalhamento por parcela: com o custo de antecipação acumulado por prazo, o total agregado
        esconde a progressão — a última parcela custa várias vezes o que custa a primeira.
      -->
      <template #row-details="row">
        <b-table
          small
          striped
          class="bordered m-0"
          :fields="detailFields"
          :items="row.item.installments_Detail || []"
        />
      </template>
    </b-table>
  </b-card-actions>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { BRow, BCol, BTable, BBadge, BAlert } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import EButton from '@/views/components/EButton.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BBadge,
    BAlert,
    BCardActions,
    EButton,
  },

  props: {
    policyId: {
      type: Number,
      default: null,
    },
    applySurcharge: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      localSaleValue: 1000,
    }
  },

  computed: {
    ...mapState(
      'pages/settings/financial/installmentPolicy/installmentPolicyMaintain',
      ['coverageLines']
    ),

    fields() {
      return [
        {
          label: '',
          key: 'detail',
          class: 'text-center',
          thStyle: { width: '50px' },
        },
        {
          label: this.$t('Parcelas'),
          key: 'installments',
          class: 'text-center',
          thStyle: { width: '110px' },
        },
        {
          label: this.$t('Valor da parcela'),
          key: 'installmentValue',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
        {
          label: this.$t('Total cobrado'),
          key: 'chargedValue',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
        {
          label: this.$t('Acréscimo'),
          key: 'surchargePercent',
          class: 'text-right',
          thStyle: { width: '110px' },
          formatter: value => this.formatPercent(value),
        },
        {
          label: this.$t('Custo efetivo'),
          key: 'effectiveCostPercent',
          class: 'text-right',
          thStyle: { width: '120px' },
          formatter: value => this.formatPercent(value),
        },
        {
          label: this.$t('Margem (p.p.)'),
          key: 'coverageMarginPoints',
          class: 'text-right',
          thStyle: { width: '130px' },
        },
        {
          label: this.$t('Margem (R$)'),
          key: 'coverageMarginValue',
          class: 'text-right',
          thStyle: { width: '130px' },
        },
        {
          label: this.$t('Líquido'),
          key: 'netValue',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
      ]
    },

    detailFields() {
      return [
        {
          label: this.$t('Parcela'),
          key: 'position',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Valor'),
          key: 'installmentValue',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
        {
          label: this.$t('Meses de antecipação'),
          key: 'anticipationMonths',
          class: 'text-center',
          thStyle: { width: '170px' },
        },
        {
          label: this.$t('Custo de antecipação'),
          key: 'anticipationCost',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
        {
          label: this.$t('Custo de operação rateado'),
          key: 'proratedOperationCost',
          class: 'text-right',
          formatter: value => this.$options.filters.currency(value),
        },
      ]
    },
  },

  async mounted() {
    if (this.policyId) {
      await this.loadCoverage()
    }
  },

  methods: {
    ...mapActions(
      'pages/settings/financial/installmentPolicy/installmentPolicyMaintain',
      {
        stFetchCoverage: 'fetchCoverage',
        stSetCoverageSaleValue: 'setCoverageSaleValue',
      }
    ),

    formatPercent(value) {
      if (value === null || value === undefined) return ''
      return `${Number(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`
    },

    async loadCoverage() {
      try {
        this.loading = true
        this.stSetCoverageSaleValue(this.localSaleValue)
        await this.stFetchCoverage()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
